import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import PersonIcon from '@atlaskit/icon/core/person';

import { ProductKeys, useProductConfig } from '../../../../../common/constants/products';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as assigneeFilter } from '../../../../../controllers/store/search/filters/assignee';

import { i18n } from './messages';

export const AssigneeFilter = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: assigneeFilter.id,
	});

	const JiraIcon = useProductConfig(ProductKeys.Jira).Icon;

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		firstSelectedOptionLabel,
		badgeLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.jiraFilterAssigneeLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.jiraFilterAssigneeLabelUnselected),
			options: suggestedOptions,
		},
	];

	const getIconBefore = () => {
		return (
			<FilterButtonIconContainer>
				<JiraIcon size="xxsmall" />
				<PersonIcon label={formatMessage(i18n.assignee)} />
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			<ChevronDownIcon label="" size="small" />
		) : (
			badgeLabel && <Badge appearance="primary">{badgeLabel}</Badge>
		);
	};

	return (
		<FilterPopupSelect
			testId="search-dialog-assignee-filter"
			filterId={assigneeFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assignee)}
			placeholder={formatMessage(i18n.jiraFilterAssigneePlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={clearOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					testId="search-dialog-assignee-filter-button"
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
				>
					{firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assignee)}
				</FilterButton>
			)}
		/>
	);
};
